import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Blogs.css';
import { fetchJson } from '../../api';
import Loader from '../Loader/Loader';

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value.replace(' ', 'T'));
  if (isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

/**
 * Render plain-text blog content: blank lines split paragraphs.
 */
const renderContent = (text) => {
  const paras = String(text || '')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  return paras.map((p, i) => <p key={i}>{p}</p>);
};

const BlogDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;
    setPost(null);
    setNotFound(false);

    fetchJson(`blogs?slug=${encodeURIComponent(slug)}`).then((data) => {
      if (!mounted) return;
      if (data && Array.isArray(data.blogs) && data.blogs.length > 0) {
        setPost(data.blogs[0]);
      } else {
        setNotFound(true);
      }
    });

    return () => {
      mounted = false;
    };
  }, [slug]);

  if (notFound) {
    return (
      <div style={{ padding: '100px 20px', textAlign: 'center' }}>
        <h1>Article not found</h1>
        <p>The article you are looking for does not exist or is not published.</p>
        <Link to="/blogs" className="about-btn">Back to Blogs</Link>
      </div>
    );
  }

  if (!post) {
    return <Loader status="Loading article" />;
  }

  const tags = post.tags
    ? post.tags.split(',').map((t) => t.trim()).filter(Boolean)
    : [];

  return (
    <div className="blog-container">
      <Helmet>
        <title>{`${post.title} | Amaanat IVF`}</title>
        {post.meta_description && (
          <meta name="description" content={post.meta_description} />
        )}
        <link
          rel="canonical"
          href={`https://amaanativf.com/blogs/${post.slug}`}
        />
        <meta property="og:title" content={`${post.title} | Amaanat IVF`} />
        {post.meta_description && (
          <meta property="og:description" content={post.meta_description} />
        )}
        {post.cover_image_url && (
          <meta property="og:image" content={post.cover_image_url} />
        )}
        <meta property="og:type" content="article" />
      </Helmet>

      {/* Hero */}
      <div className="blog-detail-hero">
        <div className="container">
          {post.category && <span className="blog-category">{post.category}</span>}
          <h1>{post.title}</h1>
          <p className="blog-date">
            {formatDate(post.published_at || post.created_at)}
            {post.author ? ` • By ${post.author}` : ''}
          </p>
        </div>
      </div>

      {post.cover_image_url && (
        <div className="container">
          <img
            src={post.cover_image_url}
            alt={post.title}
            className="blog-detail-cover"
          />
        </div>
      )}

      {/* Content */}
      <div className="blog-content blog-detail-content">
        {renderContent(post.content)}

        {Array.isArray(post.gallery) && post.gallery.length > 0 && (
          <div className="blog-gallery">
            {post.gallery.map((src, i) => (
              <a key={i} href={src} target="_blank" rel="noopener noreferrer">
                <img src={src} alt={`${post.title} — photo ${i + 1}`} loading="lazy" />
              </a>
            ))}
          </div>
        )}

        {tags.length > 0 && (
          <div className="blog-tags">
            {tags.map((tag) => (
              <span key={tag}>#{tag}</span>
            ))}
          </div>
        )}

        <div className="blog-back">
          <Link to="/blogs" className="about-btn">← Back to all articles</Link>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
